import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
  onSwitchToPasswordReset: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onSwitchToPasswordReset,
}) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({
        username,
        email: username, // API accepts both
        password,
      });

      setAuth(
        {
          id: response.user_id || 'temp',
          email: response.email || username,
          name: response.name || username
        },
        response.access_token,
        response.refresh_token
      );

      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((e: any) => e.msg).join(', '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(t('common:auth.login.error', 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('common:auth.login', 'Login')} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('common:auth.email', 'Email or Username')}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter email or username"
          required
        />

        <Input
          label={t('common:auth.password', 'Password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          showPasswordToggle
          required
        />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400 p-3 rounded-lg">
            {error}
          </div>
        )}

        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </Button>

        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={onSwitchToPasswordReset}
            className="text-sm text-cyan-600 hover:underline"
          >
            Forgot Password?
          </button>
          <div>
            <span className="text-sm text-gray-500">Don't have an account? </span>
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-sm text-cyan-600 hover:underline"
            >
              Register
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
